import { ApiErrorMessageWithTrans } from '@components/Error/ApiErrorMessageWithTrans';
import { ApiErrorPageWithTrans } from '@components/Error/ApiErrorPageWithTrans';
import { BasicHeaderNav } from '@components/Layouts/BasicHeaderNav';
import { ContentWrapperCustomerApp } from '@components/Layouts/ContentWrapperCustomerApp';
import { t } from '@lingui/core/macro';
import { Trans } from '@lingui/react/macro';
import { noddiAsync, URLKeys } from 'noddi-async';
import { NoddiButton, NoddiSpinner } from 'noddi-ui-web';
import { customerRoutes } from 'noddi-util';
import {
  SMSLogin,
  TanstackUserRegistrationForm,
  tracking,
  useAuthContext,
  useLanguageContext,
  type UserSessionData
} from 'noddi-web';
import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useCurrentBrand } from '../../stores/BrandStore/brandStore';
import { getCommonTranslations } from '../../utils/commonTranslations';
import { getErrorCodesWithTrans } from '../../utils/getErrorCodesWithTranslations';
import { CompanySignupDetailsForm, type CompanySignupDetails } from './CompanySignupDetailsForm';

export const CompanySignup = () => {
  const { userData, updateUserData } = useAuthContext();
  const { currentLanguage } = useLanguageContext();
  const navigate = useNavigate();
  const { currentBrand, brandEmail } = useCurrentBrand();
  const [companyDetails, setCompanyDetails] = useState<CompanySignupDetails | null>(null);
  const [isEditing, setIsEditing] = useState(true);

  const { data: brandData, error: brandDataError } = noddiAsync.useGet({
    type: URLKeys.getByDomainBrand,
    input: { brandDomain: currentBrand }
  });
  const {
    mutateAsync: createOrganization,
    error,
    reset,
    isPending
  } = noddiAsync.usePost({
    type: URLKeys.postExistingUserOrganizationSignup
  });

  const departmentName = companyDetails?.departmentName;

  function onOrganizationCreated(userGroupId: number) {
    navigate(customerRoutes.companySignupSuccess.getPath(), { state: { userGroupId } });
  }

  async function signupExistingUser(details: CompanySignupDetails, session: UserSessionData) {
    if (!brandData) {
      return;
    }
    await createOrganization(
      { ...details, brandId: brandData.id },
      {
        onSuccess: ({ data }) => {
          updateUserData({ ...session, user: data.user });
          tracking.track('companySignupWithExistingUser', {
            orgName: details.organizationName,
            orgNumber: details.organizationNumber
          });
          onOrganizationCreated(data.userGroupId);
        }
      }
    ).catch(() => {
      // The mutation error stays inline so company details survive a failed signup.
    });
  }

  if (brandDataError) {
    return <ApiErrorPageWithTrans error={brandDataError} />;
  }

  return (
    <div className='min-h-screen bg-primary-background-gradient'>
      <BasicHeaderNav />
      <ContentWrapperCustomerApp title={t`Company sign up`}>
        <p>
          <Trans>
            Do you need help with car washing, tire changes, or tire hotel for the vehicles in your company?
          </Trans>
        </p>
        <p className='mt-7 text-3xl font-bold text-noddiPurple'>
          <Trans>Fill out the form, and we will get in touch</Trans>
        </p>
        {!brandData ? (
          <NoddiSpinner />
        ) : isEditing ? (
          <CompanySignupDetailsForm
            registryCountry={brandData.countries[0]}
            initialValues={companyDetails ?? undefined}
            submitLabel={userData ? t`Create company account` : t`Continue`}
            onSubmit={async (details) => {
              setCompanyDetails(details);
              if (userData) {
                await signupExistingUser(details, userData);
              } else {
                setIsEditing(false);
              }
            }}
          />
        ) : (
          companyDetails && (
            <>
              <div className='mt-5 flex flex-col gap-2'>
                <p className='font-semibold'>{companyDetails.organizationName}</p>
                <p>{companyDetails.organizationNumber}</p>
                <p>
                  <Trans>Department: {departmentName}</Trans>
                </p>
                <NoddiButton
                  variant='ghost'
                  className='self-start'
                  disabledReason={isPending ? t`Creating your company account` : undefined}
                  onClick={() => {
                    reset();
                    setIsEditing(true);
                  }}
                >
                  <Trans>Edit company details</Trans>
                </NoddiButton>
              </div>
              {userData ? (
                <NoddiButton
                  className='mt-4'
                  loading={isPending}
                  onClick={() => void signupExistingUser(companyDetails, userData)}
                >
                  <Trans>Create company account</Trans>
                </NoddiButton>
              ) : (
                <SMSLogin
                  errorCodes={getErrorCodesWithTrans(brandEmail)}
                  translations={getCommonTranslations().smsLoginTranslations}
                  language={currentLanguage}
                  brandName={currentBrand}
                  onLoginSuccess={(session) => void signupExistingUser(companyDetails, session)}
                  className='mt-4'
                  trackUserIdentity={tracking.trackUserLogin}
                  userRegistrationForm={({ phoneNumber }) => (
                    <TanstackUserRegistrationForm
                      errorCodes={getErrorCodesWithTrans(brandEmail)}
                      translations={getCommonTranslations().registrationTranslations}
                      orgProperties={{
                        orgName: companyDetails.organizationName,
                        orgNumber: companyDetails.organizationNumber,
                        departmentName: companyDetails.departmentName,
                        brandId: brandData.id,
                        onCreated: onOrganizationCreated
                      }}
                      customCTAText={t`Create company account`}
                      phoneNumber={phoneNumber}
                      skipPasswordCreation
                      trackUserIdentity={(user) => {
                        tracking.trackUserIdentity(user);
                        tracking.track('companySignup', {
                          orgName: companyDetails.organizationName,
                          orgNumber: companyDetails.organizationNumber
                        });
                      }}
                    />
                  )}
                />
              )}
            </>
          )
        )}
        {error && (
          <div className='mt-4'>
            <ApiErrorMessageWithTrans error={error} />
          </div>
        )}
      </ContentWrapperCustomerApp>
    </div>
  );
};
